import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import api from '../api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Every page a permission can gate. Kept here (not just in Users.js) so
// Layout/App can both use the same source of truth for "does this user
// have this permission".
const PERMISSION_KEYS = ['dashboard', 'customers', 'loans', 'collections', 'accounts', 'expenses', 'income', 'users_page', 'my_balance'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [permissions, setPermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  function fetchPermissions(userId) {
    if (!userId) { setPermissions({}); setPermissionsLoaded(true); return; }
    api.get('/auth/permissions/' + userId)
      .then(function(res) { setPermissions(res.data || {}); })
      .catch(function() { setPermissions({}); })
      .finally(function() { setPermissionsLoaded(true); });
  }

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      // Try to restore user from localStorage
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        try {
          var u = JSON.parse(savedUser);
          setUser(u);
          fetchPermissions(u.id);
        } catch(e) {}
      }
    } else {
      setPermissionsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + authToken;
    setPermissionsLoaded(false);
    fetchPermissions(userData.id);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPermissions(null);
    setPermissionsLoaded(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Fixes a critical bug: page visibility used to be based only on role
  // (admin/collector), never on the actual per-user permission toggles set
  // in the Users screen — so unchecking a permission for a collector had no
  // real effect; they could still open the page directly by URL. Admins
  // always have full access. Collectors are gated by their saved
  // permissions, and Dashboard is hard-blocked for collectors regardless of
  // any permission value (per business rule: collectors never see the
  // company-wide dashboard).
  function hasPermission(key) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (key === 'dashboard') return false;
    if (!permissions) return false;
    return !!permissions[key];
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, permissions, permissionsLoaded, hasPermission, PERMISSION_KEYS }}>
      {children}
    </AuthContext.Provider>
  );
};
